// src/admin/components/PermissionFormModal.jsx
//
// Create/edit a permission. Replaces the always-open inline form that sat
// above the list on the old PermissionsPage — same fields, now behind the
// header's action button, matching how Connections and Accounts register a
// record (see accounts/components/RegisterAccountModal.jsx, whose
// success-Alert + fieldErrors shape this follows).
//
// One component serves both modes: `permission` null means create, otherwise
// edit that record. The form is keyed on it by the page, so switching from
// creating to editing re-seeds the fields rather than keeping stale input.

import { useState } from "react";
import PropTypes from "prop-types";
import { CheckCircle2, XCircle } from "lucide-react";
import { Modal, FormField, Input, Button, Alert, useDiscardConfirm } from "../../shared/ui";

export default function PermissionFormModal({ open, permission, onClose, onSubmit }) {
  const isEdit = Boolean(permission);

  const [values, setValues] = useState({
    name: permission?.name || "",
    description: permission?.description || "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(null);
  const [dirty, setDirty] = useState(false);

  function handleClose() {
    setErrors({});
    setSaved(null);
    setSubmitting(false);
    setDirty(false);
    onClose();
  }

  const { requestClose, confirmDialog } = useDiscardConfirm(dirty && !saved, handleClose);

  function setField(name, v) {
    setValues((prev) => ({ ...prev, [name]: v }));
    setDirty(true);
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const nextErrors = {};
    if (!values.name.trim()) {
      nextErrors.name = "Permission name is required.";
    } else if (!/^[a-z0-9_]+(:[a-z0-9_]+)+$/.test(values.name.trim())) {
      // Matches the convention every seeded row follows and that
      // require_permission("<module>:<action>") looks up by exact string —
      // a typo'd name creates a permission no route will ever check.
      nextErrors.name = "Use lowercase module:action, e.g. sow:view or conductor:engagement:create.";
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    try {
      const result = await onSubmit({
        name: values.name.trim(),
        description: values.description.trim(),
      });
      setSaved(result);
    } catch (err) {
      // A 409/422 arrives with fieldErrors from toAdminError (admin/api/
      // adminApiError.js) so "already exists" lands under the name input;
      // anything else is a banner.
      if (err?.fieldErrors) setErrors(err.fieldErrors);
      else setErrors({ _form: err?.message || "Save failed. Please try again." });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Modal
        open={open}
        title={isEdit ? "Edit permission" : "New permission"}
        onClose={requestClose}
      >
        {saved ? (
          <div className="flex flex-col gap-4">
            <Alert
              variant="success"
              icon={CheckCircle2}
              title={isEdit ? `"${saved.name}" updated` : `"${saved.name}" created`}
            >
              {isEdit
                ? "Every role already holding this permission picks up the change."
                : "Attach it to a role on the Roles page to grant it to anyone."}
            </Alert>
            <div className="flex justify-end">
              <Button variant="primary" onClick={handleClose}>Done</Button>
            </div>
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            {errors._form && (
              <Alert variant="danger" icon={XCircle} title="Couldn't save">
                {errors._form}
              </Alert>
            )}

            <FormField
              label="Name"
              required
              helperText="module:action — this exact string is what a route checks for."
              error={Boolean(errors.name)}
              errorText={errors.name}
            >
              <Input
                value={values.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="e.g. sow:view"
                autoFocus
              />
            </FormField>

            <FormField
              label="Description"
              helperText="What this lets someone do. Shown next to the checkbox when building a role."
              error={Boolean(errors.description)}
              errorText={errors.description}
            >
              <Input
                value={values.description}
                onChange={(e) => setField("description", e.target.value)}
                placeholder="e.g. View SOW analyses and their scores"
              />
            </FormField>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={handleClose}>Cancel</Button>
              <Button type="submit" variant="primary" loading={submitting}>
                {isEdit ? "Save changes" : "Create permission"}
              </Button>
            </div>
          </form>
        )}
      </Modal>
      {confirmDialog}
    </>
  );
}

PermissionFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  /** null → create mode; a record → edit mode, seeded from it. */
  permission: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  /** Receives {name, description}; resolves with the saved record, or rejects. */
  onSubmit: PropTypes.func.isRequired,
};
